import * as React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Divider,
    Slide,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const InfoIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);

const PlayIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
);

export default function UpdateFirmwareComponent({
    open,
    onClose,
    onConfirm,
    deviceCount,
    deviceType,
    currentVersions,
    userEmail,
    estimatedTime,
    latestVersion,
}) {
    // Feature flags to hide UI fields without removing the implementation
    const showEstimatedTime = false;
    const showPriorityOption = false;

    const [version, setVersion] = React.useState('');
    const [versionsAvailable, setVersionsAvailable] = React.useState(latestVersion || []);
    const [prioritize, setPrioritize] = React.useState(false);
    const { t } = useTranslation();

    React.useEffect(() => {
        setVersionsAvailable(latestVersion || []);
    }, [latestVersion]);

    const handleConfirm = () => {
        if (version) {
            onConfirm(version, prioritize);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            className="update-firmware-version"
            data-testid="update-firmware-dialog"
        >
            <DialogTitle className="update-firmware-version-title">
                <Typography>{t('translation:pages.dlm.change_firmware_version.title')}</Typography>
            </DialogTitle>

            <DialogContent>
                {/* Info Banner */}
                <Box className="info-banner">
                    <Box className="info-icon">
                        <InfoIcon />
                    </Box>
                    <Typography>{t('translation:pages.dlm.change_firmware_version.info')}</Typography>
                </Box>

                {/* Info Grid */}
                <Box className="info-grid">
                    {[
                        {
                            label: t('translation:pages.dlm.change_firmware_version.number_of_devices'),
                            value: deviceCount,
                            testId: 'device-count',
                        },
                        { label: t('translation:pages.dlm.change_firmware_version.device_type'), value: deviceType, testId: 'device-type' },
                        {
                            label: t('translation:pages.dlm.change_firmware_version.current_version'),
                            value: currentVersions?.join(', '),
                            testId: 'current-versions',
                        },
                    ].map((item) => (
                        <Box key={item.label} className="info-row">
                            <Typography className="info-label">{item.label}</Typography>
                            <Typography className="info-value" data-testid={item.testId}>{item.value}</Typography>
                        </Box>
                    ))}

                    <Box className="info-row version-select-row">
                        <Typography className="info-label">
                            {t('translation:pages.dlm.change_firmware_version.change_version')}
                        </Typography>
                        <Select value={version} onChange={(e) => setVersion(e.target.value)} displayEmpty size="small" data-testid="version-select">
                            <MenuItem value="" disabled>
                                {t('translation:pages.dlm.change_firmware_version.select_version')}
                            </MenuItem>
                            {versionsAvailable.map((el) => (
                                <MenuItem key={el} value={el}>
                                    {el}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {[
                        { label: t('translation:pages.dlm.change_firmware_version.initiated_by'), value: userEmail, testId: 'user-email' },
                        ...(showEstimatedTime
                            ? [
                                  {
                                      label: t('translation:pages.dlm.change_firmware_version.estimated_time'),
                                      value: estimatedTime,
                                      testId: 'estimated-time',
                                  },
                              ]
                            : []),
                    ].map((item) => (
                        <Box key={item.label} className="info-row">
                            <Typography className="info-label">{item.label}</Typography>
                            <Typography className="info-value" data-testid={item.testId}>{item.value}</Typography>
                        </Box>
                    ))}

                    {/* Priority Option */}
                    {showPriorityOption && (
                        <Box className="priority-row">
                            <FormControlLabel
                                control={
                                    <Checkbox checked={prioritize} onChange={(e) => setPrioritize(e.target.checked)} />
                                }
                                label={
                                    <Typography>
                                        {t('translation:pages.dlm.change_firmware_version.prioritize_update')}
                                    </Typography>
                                }
                            />
                            <Box className="info-icon-wrapper">
                                <InfoIcon />
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions className="update-firmware-versions-actions">
                <Button onClick={onClose} variant="outlined" className="cancel-btn" data-testid="dialog-close">
                    {t('translation:pages.dlm.change_firmware_version.cancel')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    color="primary"
                    variant="contained"
                    disabled={!version}
                    startIcon={<PlayIcon />}
                    className="start-update-btn"
                    data-testid="dialog-confirm"
                >
                    {t('translation:pages.dlm.change_firmware_version.start_update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
